/**
 * printPdf.js — Print a backend-generated PDF via a hidden iframe.
 *
 * Phase P1 fix for the "6-page receipt" bug.
 *
 * BEFORE (buggy):
 *    onClick={() => window.print()}          // prints the ENTIRE React DOM
 *                                             // (sidebar + dialog + tables + ...)
 *                                             // → 6 A4 pages for a 1-page receipt
 *
 * AFTER (this helper):
 *    onClick={() => printPdf(pdfUrl)}         // loads the 1-page PDF into a
 *                                             // hidden iframe and triggers the
 *                                             // browser's native PDF print
 *                                             // → 1 A4 page (matches Download &
 *                                             //   WhatsApp-share copy exactly)
 *
 * Same-origin only (our backend PDF is served from the same host as the SPA).
 * Falls back to opening the PDF in a new tab if iframe print is blocked (e.g.
 * some ad-blocker configs or mobile Safari's PDF viewer).
 */
export function printPdf(pdfUrl) {
  if (!pdfUrl) return;

  const openInNewTab = () => {
    try {
      window.open(pdfUrl, "_blank", "noopener");
    } catch (e) {
      // Absolute last resort — swallow. Print button becomes a no-op rather
      // than throwing an uncaught exception in the collection dialog.
    }
  };

  try {
    // Reuse a single hidden iframe across successive prints in the same session
    // so we don't leak DOM nodes if the cashier prints many receipts in a row.
    const IFRAME_ID = "receipt-print-iframe";
    const existing = document.getElementById(IFRAME_ID);
    if (existing) existing.remove();

    const iframe = document.createElement("iframe");
    iframe.id = IFRAME_ID;
    iframe.setAttribute("aria-hidden", "true");
    iframe.style.position = "fixed";
    iframe.style.right = "0";
    iframe.style.bottom = "0";
    iframe.style.width = "0";
    iframe.style.height = "0";
    iframe.style.border = "0";
    iframe.src = pdfUrl;

    let printed = false;
    iframe.onload = () => {
      // Give the browser's PDF viewer a beat to finish laying out before
      // asking it to open the print dialog. Without this, some Chrome
      // versions open the print dialog before the PDF is ready and print
      // a blank page.
      setTimeout(() => {
        try {
          if (printed) return;
          printed = true;
          iframe.contentWindow.focus();
          iframe.contentWindow.print();
        } catch (err) {
          // Cross-origin block (shouldn't happen — same origin — but defend)
          // or PDF-viewer-print unsupported → new-tab fallback.
          openInNewTab();
        }
      }, 400);
    };
    iframe.onerror = openInNewTab;

    document.body.appendChild(iframe);

    // Best-effort cleanup after 2 minutes. The iframe may still be alive if
    // the user leaves the print dialog open, but any subsequent print call
    // removes the previous one first (see `existing?.remove()` above).
    setTimeout(() => {
      try {
        const node = document.getElementById(IFRAME_ID);
        if (node && node === iframe) node.remove();
      } catch (e) {
        // ignore
      }
    }, 120000);
  } catch (err) {
    openInNewTab();
  }
}
